import React, {useEffect, useMemo, useState} from 'react'
import {useQueries} from '@tanstack/react-query'
import {fetchOneEntry, Content} from '@builder.io/sdk-react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {customComponents} from 'hec/builder'
import {cn} from '@/lib/utils'
import {useMediaQueryWithBuilder} from './useMediaQueryWithBuilder'

function getSpanForCurrentBreakpoint(item, {isMobile, isTablet, isDesktop, isLargeDesktop}) {
    if (!item) return null
    if (isLargeDesktop) return parseInt(item.largeDesktopSpan, 10) || null
    if (isDesktop) return parseInt(item.desktopSpan, 10) || null
    if (isTablet) return parseInt(item.tabletSpan, 10) || null
    if (isMobile) return parseInt(item.mobileSpan, 10) || null
    return null
}

export const useInjectableGridContent = (injectableItems = [], gridItemClassnames = '') => {
    const {isMobile, isTablet, isDesktop, isLargeDesktop} = useMediaQueryWithBuilder()
    const [transformedContent, setTransformedContent] = useState([])
    const apiKey = getConfig().app.builder.api

    // Only the items needing Builder fetch
    const itemsNeedingFetch = useMemo(() => {
        return injectableItems.filter((item) => {
            const contentData = item?.content?.content?.value?.data
            const isBuilder = contentData?.isExternalContentSource === false
            const missingContent = !contentData?.builderContent?.value
            return isBuilder && missingContent
        })
    }, [injectableItems])

    const fetchQueries = useQueries({
        queries: itemsNeedingFetch.map((item) => {
            const contentData = item?.content?.content?.value?.data
            const id = contentData?.builderContent?.id
            const model = contentData?.builderContent?.model
            return {
                queryKey: ['injectable-content', id, model],
                queryFn: () => fetchOneEntry({apiKey, model, query: {id}}),
                enabled: !!id && !!model
            }
        })
    })

    const isAnyQueriesLoading = fetchQueries.some((q) => q.isLoading)

    useEffect(() => {
        if (isAnyQueriesLoading || injectableItems.length === 0) return

        const breakpointFlags = {isMobile, isTablet, isDesktop, isLargeDesktop}

        const transformedData = injectableItems
            .map((item) => {
                const contentItem = item?.content
                const content = contentItem?.content?.value?.data
                const isBuilder = content?.isExternalContentSource === false
                const id = content?.builderContent?.id
                const model = content?.builderContent?.model
                const builderContent = content?.builderContent?.value

                const span = getSpanForCurrentBreakpoint(contentItem, breakpointFlags)
                if (span === 0 || span == null) return null

                const position = contentItem?.position ?? 0
                const wrapperProps = {
                    className: cn('grid-item overflow-hidden relative', gridItemClassnames),
                    style: {gridColumn: `span ${span}`}
                }

                // Get fetched Builder content
                const matchingQuery = fetchQueries.find((q) => {
                    return q?.data?.id === id
                })

                if (isBuilder && (builderContent || matchingQuery?.data)) {
                    return {
                        index: position,
                        content: (
                            <div {...wrapperProps}>
                                <Content
                                    content={builderContent || matchingQuery.data}
                                    model={model}
                                    apiKey={apiKey}
                                    customComponents={customComponents}
                                    data-testid={'useInjectableGridContent'}
                                />
                            </div>
                        )
                    }
                }

                if (!isBuilder && content?.embeddedContent) {
                    return {
                        index: position,
                        content: (
                            <div
                                {...wrapperProps}
                                dangerouslySetInnerHTML={{__html: content.embeddedContent}}
                            />
                        )
                    }
                }

                return null
            })
            .filter(Boolean)

        setTransformedContent(transformedData)
    }, [
        injectableItems,
        isAnyQueriesLoading,
        apiKey,
        gridItemClassnames,
        isMobile,
        isTablet,
        isDesktop,
        isLargeDesktop
    ])

    return transformedContent
}
